import { useMutation } from '@tanstack/react-query';
import { Stack, TableCell, Typography } from '@mui/material';
import { Badge, dateTime, HeadCell, SimpleTable, TitlePanels, ToAuditButton } from '@shared';

import { AuditQueueTablePropsType, ColumnsIdsInQueueTable } from './AuditQueue.types';
import { startAudit } from 'App/api/services/api-audit.service';
import { RouteEnum, useGotoRoute } from 'App';
import { Stars } from '@mui/icons-material';

const headCellsAuditQueueTable: readonly HeadCell<ColumnsIdsInQueueTable>[] = [
  {
    id: 'partner',
    label: 'Partner',
  },
  {
    id: 'course',
    label: 'Course',
  },
  {
    id: 'exam',
    label: 'Exam',
  },
  {
    id: 'student',
    label: 'Student',
  },
  {
    id: 'exam start',
    label: 'Exam Start',
  },
  {
    id: 'exam end',
    label: 'Exam End',
  },
  {
    id: 'duration',
    label: 'Duration',
  },
  {
    id: 'start button',
    label: '',
  },
];
const LIMIT = 20;

export function AuditsInQueueTable({
  totalResults,
  auditsInQueue,
  onPageChange,
  isFetching,
}: AuditQueueTablePropsType) {
  const goToRoute = useGotoRoute(RouteEnum.EnhancedAudit);

  const mutation = useMutation({
    mutationFn: (examSessionUuid: string) => {
      return startAudit(examSessionUuid);
    },
    onSuccess: (data) => goToRoute({ audit: data.auditUuid }),
  });

  const pagination = totalResults > auditsInQueue.length;
  const totalPages = pagination ? Math.ceil(totalResults / LIMIT) : undefined;
  return (
    <section>
      <TitlePanels titles={['Audit Queue']} counter={totalResults} />
      <SimpleTable
        columns={headCellsAuditQueueTable}
        rows={auditsInQueue}
        entityName={'audit'}
        pagination={pagination}
        totalPages={totalPages}
        onPageChange={onPageChange}
        isLoading={isFetching}
        renderElems={({
          examStartTime,
          examEndTime,
          examDuration,
          specialPartner,
          uuid,
          ...rest
        }) => {
          const nameEntities = Object.entries(rest);
          return (
            <>
              {nameEntities.map(([keyName, value]) => (
                <TableCell key={keyName}>
                  <Stack direction='row' alignItems='center' justifyContent='space-between'>
                    <Typography component='span' variant='tableCell'>
                      {value}
                    </Typography>
                    {keyName === 'partnerName' && specialPartner && (
                      <Badge title='Special partner' icon={<Stars color='primary' />} />
                    )}
                  </Stack>
                </TableCell>
              ))}
              <TableCell key={'examStartTime'}>
                <Typography component='time' variant='tableCell'>
                  {dateTime(examStartTime, 'examStartTime')}
                </Typography>
              </TableCell>
              <TableCell key={'examEndTime'}>
                <Typography component='time' variant='tableCell'>
                  {dateTime(examEndTime, 'examEndTime')}
                </Typography>
              </TableCell>
              <TableCell key={'examDuration'}>
                <Typography component='time' variant='tableCell'>
                  {examDuration}
                </Typography>
              </TableCell>
              <TableCell>
                <ToAuditButton uuid={uuid} title={'Start Auditing'} onClick={mutation.mutate} />
              </TableCell>
            </>
          );
        }}
      />
    </section>
  );
}
